<template>
    <div class="list-lag">
        <KHeader heading="Lag">
            <KButton variant="hollow" size="slim" :icon-path="PlusCircleBlue" @click="() => openForm()">
                Skapa nytt lag
            </KButton>
        </KHeader>

        <KFilter name="lag-filter" :schema="filterSchema" @submit="onFilter" />

        <KTable
            paginator
            alwaysShowPaginator
            name="lag-table"
            :columns="columns"
            :actions="tableActions"
            :value="rows"
            :loading="isLoading"
            :totalRecords="count"
            :first="first"
            @page="onPage"
        />
    </div>
</template>

<script lang="ts">
    import tabIcon from "@/assets/narvarostatistik.svg";

    export default {
        name: "ListLag",
        tabName: "Lag",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
    import { openDialog } from "@/utils";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import KButton from "@/components/Button.vue";
    import KHeader from "@/components/KHeader.vue";
    import PlusCircleBlue from "@/assets/plus_circle_blue.svg";
    import FormLag from "@/tabs/Skreg/Lag/FormLag.vue";
    import { useStore } from "@/store";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";

    const store = useStore();

    const isLoading = ref(false);
    const filterParams = ref<Record<string, any>>({});
    const subsetName = ref("default");
    const page = ref(1);
    const limit = ref(20);
    // Antalet rader som finns för subsetet, som paginatorn räknar sidor på
    const count = computed(() => store.getters["skreg/lag/lagSubsetTotal"]({ subsetName: subsetName.value }));

    const first = computed(() => (page.value - 1) * limit.value);

    const newlyCreated = computed(() =>
        store.getters["skreg/lag/lagSubset"]({ subsetName: "newlyCreated", offset: 0, limit: 0, toArray: false })
    );
    const newlyUpdated = computed(() =>
        store.getters["skreg/lag/lagSubset"]({ subsetName: "newlyUpdated", offset: 0, limit: 0, toArray: false })
    );

    watch([() => newlyCreated.value.size, () => newlyUpdated.value.size], () => {
        fetchList();
    });

    const rows = computed(() =>
        store.getters["skreg/lag/lagSubset"]({
            subsetName: subsetName.value,
            offset: first.value,
            limit: limit.value,
        }).map((l: any) => ({
            ...l,
            _idrott: l.idrott_display?.namn ?? "",
            _plats: l.plats_display?.namn ?? "",
            tableInfo: {
                isNewlyCreated: !!newlyCreated.value.get(l.id),
                isNewlyUpdated: !!newlyUpdated.value.get(l.id),
            },
        }))
    );

    const filterSchema = generateSchema({
        addToAll: { ignoreEmptyData: true },
        schema: [
            { component: "KText", name: "namn", label: "Namn" },
            { component: "IdrottAutocomplete", name: "idrott", label: "Idrott" },
        ],
    });

    const columns = [
        { field: "namn", header: "Namn" },
        { field: "_idrott", header: "Idrott" },
        { field: "division", header: "Division" },
        { field: "_plats", header: "Plats" },
    ];

    const tableActions = [
        {
            title: "Redigera lag",
            show: () => true,
            callback: ({ data }: { data: any }) => openForm(data),
        },
    ];

    const fetchList = async () => {
        isLoading.value = true;
        try {
            const { subsetName: fetchedSubset } = await store.dispatch("skreg/lag/fetchLag", {
                page: page.value,
                limit: limit.value,
                filterParams: filterParams.value,
            });
            subsetName.value = fetchedSubset;
        } catch {
            openDialog("Kunde inte hämta lag.", "warning");
        } finally {
            isLoading.value = false;
        }
    };

    const onFilter = ({ formData }: { formData: any }) => {
        filterParams.value = formData;
        page.value = 1;
        fetchList();
    };

    const onPage = ({ page: newPage, rows: newRows }: { page: number; rows: number }) => {
        page.value = newPage;
        limit.value = newRows;
        fetchList();
    };

    const openForm = (item = undefined) => {
        store.dispatch("tabs/openTab", {
            component: FormLag,
            parameters: item,
        });
    };

    onMounted(async () => {
        await store.dispatch("skreg/lag/addSubscriber");
        await fetchList();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("skreg/lag/removeSubscriber");
    });
</script>

<style lang="scss" scoped></style>
